import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import api from "../../../../conf/api";
import { WcaBadge } from "./constants";
import useS3Upload from "./useS3Upload";
import { SelectedFileCard, UploadDropzone, UploadProgress } from "./UploadPanel";

/**
 * Edit a compilation's details, optionally swapping the video behind it.
 *
 * Replacing the video sends the new one browser-direct to S3 (same path as
 * creation) and resets the compilation to pending — the previous approval was
 * for the previous file. The old staged upload and local file are cleaned up
 * server-side; an already-published S3 object is deliberately left in place,
 * since a live feed may still be serving it.
 */
export default function EditCompilationDialog({ open, compilation, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [keywords, setKeywords] = useState("");
  const [replacing, setReplacing] = useState(false);
  const [newFile, setNewFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const upload = useS3Upload();
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  useEffect(() => {
    if (open && compilation) {
      setTitle(compilation.title || "");
      setDescription(compilation.description || "");
      setKeywords((compilation.keywords || []).join(", "));
      setReplacing(false);
      setNewFile(null);
      upload.reset();
    }
    // upload.reset is stable (useCallback with no deps); re-running this on
    // its identity would clear the panel on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, compilation]);

  if (!compilation) return null;

  const busy = saving || upload.isBusy;

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/mrss/compilations/${compilation.id}/`, {
        title: title.trim(),
        description,
        keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
      });
      if (newFile) {
        const stagedKey = await upload.start(newFile, "video");
        await api.post(`/mrss/compilations/${compilation.id}/replace-video/`, {
          staged_video_key: stagedKey,
        });
        toast.success("Compilation updated — the new video needs approval again");
      } else {
        toast.success("Compilation updated");
      }
      onSaved && onSaved();
    } catch (err) {
      if (upload.state.phase !== "error" && upload.state.phase !== "cancelled") {
        toast.error(err.response?.data?.error || err.message || "Failed to save changes");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 } } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, px: { xs: 2, sm: 3 } }}>
        <EditIcon sx={{ color: "#059669", flexShrink: 0 }} />
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: 17, sm: 20 } }}>
          Edit Compilation
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" gap={1} mb={2.5} flexWrap="wrap">
          <WcaBadge size="small">{compilation.unique_key}</WcaBadge>
          <Typography variant="caption" color="text.secondary">
            The WCA id and brand can't be changed after creation.
          </Typography>
        </Box>

        <TextField
          fullWidth label="Title" value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2.5 }} required
        />
        <TextField
          fullWidth label="Description" multiline rows={3} value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2.5 }}
        />
        <TextField
          fullWidth label="Keywords (comma separated)" value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          sx={{ mb: 2.5 }}
        />

        <Divider sx={{ mb: 2 }} />

        {!replacing && !newFile ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              gap: { xs: 1.25, sm: 2 },
            }}
          >
            <Typography variant="body2" color="text.secondary">
              The current video stays as-is unless you replace it.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<SwapHorizIcon fontSize="small" />}
              onClick={() => setReplacing(true)}
              sx={{
                borderRadius: 2, textTransform: "none", whiteSpace: "nowrap",
                borderColor: "#e2e8f0", minHeight: 40, flexShrink: 0,
              }}
            >
              Replace Video
            </Button>
          </Box>
        ) : newFile ? (
          <>
            <SelectedFileCard
              file={newFile}
              onRemove={() => { setNewFile(null); setReplacing(false); upload.reset(); }}
              disabled={busy}
            />
            <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
              Replacing the video sends this compilation back to <b>Awaiting approval</b> — the
              previous approval was for the previous file.
            </Alert>
          </>
        ) : (
          <UploadDropzone onFile={setNewFile} compact />
        )}

        <UploadProgress state={upload.state} onCancel={upload.cancel} onRetry={handleSave} />
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2.5 }, pt: 1.5, gap: 1, flexWrap: "wrap",
          borderTop: { xs: "1px solid #eef2f6", sm: "none" },
          "& .MuiButton-root": { minHeight: 40, ml: "0 !important" },
        }}
      >
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained" disableElevation color="success" onClick={handleSave}
          disabled={busy || !title.trim()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          {upload.isBusy ? "Uploading…" : saving ? "Saving…" : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
